/**
 * Canonical confidence semantics for the operator AI surfaces.
 *
 * One source of truth for:
 *   - tier thresholds          (high / medium / low)
 *   - tier copy                (label + operator guidance)
 *   - tier color tokens        (bar / text / surface tint)
 *
 * Every AI surface (issue analysis, inbox classification, KB answers,
 * patch reviews, intake bot) reads these — so an operator who learns
 * "yellow means review recommended" once gets that meaning everywhere.
 *
 * The thresholds match what `ConfidenceIndicator` was already using.
 * That component now reads them from here so the two never drift.
 *
 *   ≥ 0.85  → high     "Safe to review quickly"
 *   ≥ 0.70  → medium   "Review recommended"
 *   <  0.70 → low      "Manual verification required"
 */

export type ConfidenceTier = "high" | "medium" | "low";

export const CONFIDENCE_THRESHOLDS = {
  high: 0.85,
  medium: 0.7,
} as const;

export type ConfidenceTierMeta = {
  /** Tier slug. */
  tier: ConfidenceTier;
  /** Short label ("High", "Medium", "Low"). */
  label: string;
  /** Operator guidance — one sentence, imperative. */
  guidance: string;
  /** Tailwind color tokens (foreground + bar background). */
  textCls: string;
  barCls: string;
  /** Tinted surface for cards/banners. */
  surfaceCls: string;
};

export const CONFIDENCE_META: Record<ConfidenceTier, ConfidenceTierMeta> = {
  high: {
    tier: "high",
    label: "High",
    guidance: "Safe to review quickly",
    textCls: "text-status-low",
    barCls: "bg-status-low",
    surfaceCls: "border-status-low/20 bg-status-low/5",
  },
  medium: {
    tier: "medium",
    label: "Medium",
    guidance: "Review recommended",
    textCls: "text-status-medium",
    barCls: "bg-status-medium",
    surfaceCls: "border-status-medium/20 bg-status-medium/5",
  },
  low: {
    tier: "low",
    label: "Low",
    guidance: "Manual verification required",
    textCls: "text-status-critical",
    barCls: "bg-status-critical",
    surfaceCls: "border-status-critical/20 bg-status-critical/5",
  },
};

/**
 * Bucket a 0..1 confidence score into a tier.
 *
 * NaN / non-finite values fall through to "low" — better to under-trust
 * a missing signal than to render the surface as if the model were
 * confident.
 */
export function tierForConfidence(value: number): ConfidenceTier {
  if (!Number.isFinite(value)) return "low";
  if (value >= CONFIDENCE_THRESHOLDS.high) return "high";
  if (value >= CONFIDENCE_THRESHOLDS.medium) return "medium";
  return "low";
}

export function metaForConfidence(value: number): ConfidenceTierMeta {
  return CONFIDENCE_META[tierForConfidence(value)];
}

/** Render-friendly clamping. */
export function clampConfidence(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

/** Percentage as integer 0..100, clamped. */
export function confidencePct(value: number): number {
  return Math.round(clampConfidence(value) * 100);
}
